/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  signal
} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {NgSelectComponent, NgSelectModule} from '@ng-select/ng-select';
import {Subject, Subscription, timer} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import template from './adn-select.component.html';
import {StateService} from '@uirouter/core';

export interface AdnSelectSearchElement {
  search: string;
  open?: boolean;
}

export interface AdnSelectItemLink {
  stateName: string;
  params?: any;
}

function coerceBooleanAttr(value: any, defaultValue = false): boolean {
  if (value === true || value === 'true') {
    return true;
  }
  if (value === false || value === 'false') {
    return false;
  }
  // Bare `multiple` / `allow-clear` attributes arrive as ''.
  if (value === '' || value === 'multiple') {
    return true;
  }
  return defaultValue;
}

@Component({
  standalone: true,
  selector: 'adn-select',
  imports: [CommonModule, FormsModule, NgSelectModule],
  template,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AdnSelectComponent),
    multi: true
  }]
})
export class AdnSelectComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() items: any[] = [];
  @Input() search?: (searchElement: AdnSelectSearchElement, ...args: any[]) => void;
  @Input() searchArgs?: any;
  @Input() placeholder = '';
  @Input() bindLabel = 'name';
  @Input() bindValue?: string;
  @Input() set labelFallback(value: string) {
    this.labelFallbackState.set(value || '');
  }
  get labelFallback(): string {
    return this.labelFallbackState();
  }
  @Input() labelFields?: string;
  @Input() labelSuffixField?: string;
  @Input() notFoundText = 'No matching results found';
  @Input() itemLink?: (item: any) => AdnSelectItemLink | null | undefined;

  @Input() set searchable(value: boolean | string | undefined) {
    if (value === undefined || value === null) {
      this._searchable = true;
      return;
    }
    this._searchable = coerceBooleanAttr(value);
  }
  get searchable(): boolean {
    return this._searchable;
  }

  @Input() set multiple(value: boolean | string) {
    this._multiple = coerceBooleanAttr(value);
  }
  get multiple(): boolean {
    return this._multiple;
  }

  @Input() set allowClear(value: boolean | string) {
    this._allowClear = coerceBooleanAttr(value);
  }
  get allowClear(): boolean {
    return this._allowClear;
  }

  @Input() set chipStyle(value: boolean | string) {
    this._chipStyle = coerceBooleanAttr(value);
  }
  get chipStyle(): boolean {
    return this._chipStyle;
  }

  @Input() set refreshDelay(value: number | string) {
    this._refreshDelay = typeof value === 'number' ? value : parseInt(`${value}`, 10) || 300;
  }
  get refreshDelay(): number {
    return this._refreshDelay;
  }

  @Input() set disabled(value: boolean | string) {
    this._disabled = value === true || value === 'true';
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Output() select = new EventEmitter<any>();

  @ViewChild('ngSelect') ngSelect?: NgSelectComponent;

  readonly ngSelectTypeahead$ = new Subject<string>();
  readonly hiddenSearchInputAttrs = {tabindex: '-1', 'aria-hidden': 'true'};

  headerSearchKeyDownFn = (event: KeyboardEvent): boolean => {
    if (!this.searchable || !this.dropdownOpen()) {
      return true;
    }
    const input = this.findHeaderSearchInput();
    if (!input) {
      return true;
    }
    if (document.activeElement === input) {
      return false;
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      input.focus();
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? input.value.length;
      const nextValue = `${input.value.slice(0, start)}${event.key}${input.value.slice(end)}`;
      input.value = nextValue;
      input.setSelectionRange(start + 1, start + 1);
      this.onHeaderSearchInput(nextValue);
      return false;
    }
    return true;
  };

  readonly displayItems = signal<any[]>([]);
  readonly innerValue = signal<any>(undefined);
  readonly isDisabled = signal(false);
  readonly dropdownOpen = signal(false);
  readonly searchLoading = signal(false);
  readonly headerSearchText = signal('');

  private _allowClear = false;
  private _chipStyle = false;
  private _multiple = false;
  private _refreshDelay = 300;
  private _disabled = false;
  private _searchable = true;
  private readonly labelFallbackState = signal('');

  private readonly destroy$ = new Subject<void>();
  private labelFieldList: string[] = [];
  private searchRequestId = 0;
  private backendSearchSubscription?: Subscription;
  private pendingSearchTerm = '';
  private lastBackendSearchTerm?: string;
  private onChange: (value: any) => void = () => undefined;
  onTouched: () => void = () => undefined;

  constructor(
    private hostElement: ElementRef<HTMLElement>,
    private readonly $state: StateService
  ) {
  }

  ngOnInit(): void {
    this.labelFieldList = this.parseLabelFields(this.labelFields);
    this.refreshDisplayItems();

    // ng-select refuses to open with empty items unless typeahead mode is active.
    this.ngSelectTypeahead$.pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.labelFields) {
      this.labelFieldList = this.parseLabelFields(this.labelFields);
    }
    if (changes.items || changes.bindValue || changes.bindLabel) {
      this.refreshDisplayItems();
      if (changes.items && this.searchLoading()) {
        this.setSearchLoading(false);
      }
    }
    if (changes.refreshDelay && !changes.refreshDelay.firstChange && this.pendingSearchTerm !== undefined) {
      this.queueBackendSearch(this.pendingSearchTerm);
    }
  }

  ngOnDestroy(): void {
    this.cancelQueuedBackendSearch();
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: any): void {
    if (this._multiple && value !== null && value !== undefined && !Array.isArray(value)) {
      this.innerValue.set([value]);
    } else {
      this.innerValue.set(value);
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  compareWith = (left: any, right: any): boolean => {
    if (left === right) {
      return true;
    }
    if (left === null || left === undefined || right === null || right === undefined) {
      return false;
    }
    if (this.bindValue) {
      const leftValue = typeof left === 'object' ? left[this.bindValue] : left;
      const rightValue = typeof right === 'object' ? right[this.bindValue] : right;
      return leftValue === rightValue;
    }
    if (typeof left === 'object' && typeof right === 'object') {
      if (left.id !== undefined && right.id !== undefined) {
        return left.id === right.id;
      }
    }
    return false;
  };

  onInnerValueChange(value: any): void {
    this.innerValue.set(value);
    this.onChange(value);
    const selectedItem = this.resolveItem(value);
    this.select.emit({
      item: selectedItem,
      model: value,
      $item: selectedItem,
      $model: value
    });
  }

  onDropdownOpen(): void {
    this.dropdownOpen.set(true);
    this.headerSearchText.set('');
    this.lastBackendSearchTerm = undefined;
    if (this.search) {
      this.ngSelect?.blur();
      this.runBackendSearchImmediately('');
    }
    if (this.searchable) {
      this.scheduleHeaderSearchFocus();
    }
  }

  private scheduleHeaderSearchFocus(): void {
    timer(50).pipe(take(1)).subscribe(() => {
      if (this.dropdownOpen() && this.ngSelect?.isOpen) {
        this.observeHeaderSearchInput();
      }
    });
  }

  private observeHeaderSearchInput(): void {
    const root = this.hostElement.nativeElement;
    if (root.querySelector('.adn-select-search-input')) {
      if (this.dropdownOpen() && this.ngSelect?.isOpen) {
        this.focusHeaderSearchInput();
      }
      return;
    }
    const observer = new MutationObserver(() => {
      if (this.findHeaderSearchInput()) {
        observer.disconnect();
        if (this.dropdownOpen() && this.ngSelect?.isOpen) {
          this.focusHeaderSearchInput();
        }
      }
    });
    observer.observe(root, {childList: true, subtree: true});
    timer(500).pipe(take(1)).subscribe(() => observer.disconnect());
  }

  private focusHeaderSearchInput(attempt = 0): void {
    if (!this.dropdownOpen() || !this.ngSelect?.isOpen) {
      return;
    }
    const input = this.findHeaderSearchInput();
    if (input) {
      input.focus({preventScroll: true});
      return;
    }
    if (attempt < 12) {
      requestAnimationFrame(() => this.focusHeaderSearchInput(attempt + 1));
    }
  }

  private findHeaderSearchInput(): HTMLInputElement | null {
    return this.hostElement.nativeElement.querySelector('.adn-select-search-input');
  }

  onHeaderSearchInput(value: string): void {
    this.headerSearchText.set(value);
    if (this.search) {
      this.queueBackendSearch(value);
      return;
    }
    this.ngSelect?.filter(value);
  }

  onInnerSearch(): void {
    // ng-select emits (search) when its hidden input calls filter(); remote search uses the header input only.
  }

  onHostPointerEvent(event: MouseEvent): void {
    if (this.hostElement.nativeElement.closest('.popover, .popover-content')) {
      event.stopPropagation();
    }
  }

  onHeaderSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        this.selectHighlightedOption();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.ngSelect?.close();
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.ngSelect?.handleKeyCode(event);
        break;
      default:
        event.stopPropagation();
    }
  }

  private selectHighlightedOption(): void {
    const ngSelect = this.ngSelect;
    if (!ngSelect?.isOpen) {
      return;
    }
    const markedItem = ngSelect.itemsList?.markedItem;
    if (markedItem) {
      ngSelect.toggleItem(markedItem);
      if (this.multiple && this.searchable) {
        this.scheduleHeaderSearchFocus();
      }
      return;
    }
    if (ngSelect.showAddTag) {
      ngSelect.selectTag();
    }
  }

  onDropdownClose(): void {
    this.dropdownOpen.set(false);
    this.headerSearchText.set('');
    this.cancelQueuedBackendSearch();
    this.setSearchLoading(false);
    this.lastBackendSearchTerm = undefined;
  }

  formatItemLabel(item: any): string {
    const resolved = this.resolveItem(item);
    if (!resolved) {
      return this.labelFallback || '';
    }
    if (typeof resolved === 'string' || typeof resolved === 'number') {
      return this.labelFallback || `${resolved}`;
    }

    for (const field of this.labelFieldList) {
      if (resolved[field]) {
        return `${resolved[field]}`;
      }
    }

    let label = resolved[this.bindLabel];
    if (!label && this.labelFallback) {
      label = this.labelFallback;
    } else if (!label && this.bindValue && resolved[this.bindValue] !== undefined) {
      label = `${resolved[this.bindValue]}`;
    }
    if (this.labelSuffixField && resolved[this.labelSuffixField]) {
      label = `${label || ''} (${resolved[this.labelSuffixField]})`.trim();
    }
    if (!label) {
      return this.labelFallback || '';
    }
    return `${label}`;
  }

  hasItemLink(item: any): boolean {
    return !!this.resolveItemLink(item);
  }

  getItemHref(item: any): string | null {
    const link = this.resolveItemLink(item);
    if (!link?.stateName) {
      return null;
    }
    return this.$state.href(link.stateName, link.params || {}, {inherit: false});
  }

  resolveItem(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }
    if (typeof value === 'object') {
      if (this.bindLabel && !value[this.bindLabel] && this.labelFallback) {
        return {
          ...value,
          [this.bindLabel]: this.labelFallback
        };
      }
      return value;
    }
    if (!this.bindValue) {
      return value;
    }
    const match = this.normalizeItems(this.items).find((item) => item?.[this.bindValue] === value);
    if (match) {
      return match;
    }
    return {
      [this.bindValue]: value,
      [this.bindLabel]: this.labelFallback
    };
  }

  private resolveItemLink(item: any): AdnSelectItemLink | null {
    if (!this.itemLink) {
      return null;
    }
    return this.itemLink(this.resolveItem(item)) || null;
  }

  private parseLabelFields(labelFields?: string): string[] {
    if (!labelFields) {
      return [];
    }
    return labelFields.split(',').map((field) => field.trim()).filter(Boolean);
  }

  private normalizeItems(items: any): any[] {
    if (Array.isArray(items)) {
      return items;
    }
    if (items && typeof items === 'object' && Array.isArray(items.results)) {
      return items.results;
    }
    return [];
  }

  private refreshDisplayItems(): void {
    this.displayItems.set(this.normalizeItems(this.items).filter((item) => this.isSelectableItem(item)));
  }

  private isSelectableItem(item: any): boolean {
    if (!item) {
      return false;
    }
    if (this.bindValue) {
      return item[this.bindValue] !== undefined && item[this.bindValue] !== null;
    }
    if (item.id !== undefined) {
      return true;
    }
    return false;
  }

  private queueBackendSearch(term: string): void {
    this.pendingSearchTerm = term;
    this.setSearchLoading(true);
    this.cancelQueuedBackendSearch();
    this.backendSearchSubscription = timer(this.refreshDelay).pipe(take(1)).subscribe(() => {
      this.backendSearchSubscription = undefined;
      if (!this.search || !this.dropdownOpen()) {
        return;
      }
      this.runBackendSearch(this.pendingSearchTerm);
    });
  }

  private runBackendSearchImmediately(term: string): void {
    this.pendingSearchTerm = term;
    this.cancelQueuedBackendSearch();
    this.lastBackendSearchTerm = undefined;
    this.runBackendSearch(term);
  }

  private cancelQueuedBackendSearch(): void {
    if (this.backendSearchSubscription) {
      this.backendSearchSubscription.unsubscribe();
      this.backendSearchSubscription = undefined;
    }
  }

  private runBackendSearch(term: string): void {
    if (!this.search) {
      return;
    }
    const normalizedTerm = term || '';
    if (normalizedTerm === this.lastBackendSearchTerm) {
      this.setSearchLoading(false);
      return;
    }
    this.lastBackendSearchTerm = normalizedTerm;
    const requestId = ++this.searchRequestId;
    this.setSearchLoading(true);
    timer(10000).pipe(take(1)).subscribe(() => {
      if (this.searchLoading() && this.searchRequestId === requestId) {
        this.setSearchLoading(false);
      }
    });
    const searchElement: AdnSelectSearchElement = {
      search: normalizedTerm,
      open: this.dropdownOpen()
    };
    if (this.searchArgs !== undefined) {
      this.search(searchElement, this.searchArgs);
      return;
    }
    this.search(searchElement);
  }

  private setSearchLoading(loading: boolean): void {
    if (this.searchLoading() === loading) {
      return;
    }
    this.searchLoading.set(loading);
  }
}
